/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useResetTestletSessionMutation } from '../../mutations';
import ExtendTimeConfirmationDialog from '../../Components/ExtendTimeConfirmationDialog';

type SessionManagementContextType = {
  selectedStudents: number[];
  setSelectedStudents: (selectedStudents: number[]) => void;
  handleResetTestletSession: () => void;
  isResettingTestletSession: boolean;
  setIsExtendTimeDialogOpen: (isOpen: boolean) => void;
  isExtendTimeDialogOpen: boolean;
};

const SessionManagementContext = createContext<SessionManagementContextType>({
  selectedStudents: [],
  setSelectedStudents: () => {},
  handleResetTestletSession: () => {},
  isResettingTestletSession: false,
  setIsExtendTimeDialogOpen: () => {},
  isExtendTimeDialogOpen: false,
});

export const SessionManagementProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { testletId } = useParams();
  const [isExtendTimeDialogOpen, setIsExtendTimeDialogOpen] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const { mutate: resetTestletSession, isLoading: isResettingTestletSession } =
    useResetTestletSessionMutation(testletId);

  const handleResetTestletSession = React.useCallback(() => {
    resetTestletSession(selectedStudents, {
      onSuccess: () => {
        setSelectedStudents([]);
      },
    });
  }, [resetTestletSession, selectedStudents]);

  return (
    <SessionManagementContext.Provider
      value={useMemo(
        () => ({
          selectedStudents,
          setSelectedStudents,
          handleResetTestletSession,
          isResettingTestletSession,
          setIsExtendTimeDialogOpen,
          isExtendTimeDialogOpen,
        }),
        [
          selectedStudents,
          handleResetTestletSession,
          isResettingTestletSession,
          isExtendTimeDialogOpen,
        ],
      )}
    >
      {children}
      <ExtendTimeConfirmationDialog
        open={isExtendTimeDialogOpen}
        onClose={() => setIsExtendTimeDialogOpen(false)}
        onConfirm={() => {
          setIsExtendTimeDialogOpen(false);
          setSelectedStudents([]);
        }}
        data={{
          studentIds: selectedStudents,
          testletId,
        }}
      />
    </SessionManagementContext.Provider>
  );
};

export default SessionManagementProvider;

export const useSessionManagement = () => {
  const context = useContext(SessionManagementContext);
  if (!context) {
    throw new Error(
      'useSessionManagement must be used within a SessionManagementProvider',
    );
  }
  return context;
};
